import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  EnvelopeIcon as MailIcon,
  PhoneIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import Logo from '../common/Logo';
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
} from 'react-icons/fa';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    platform: {
      title: t('footer.sections.platform.title'),
      links: [
        { name: t('footer.sections.platform.home'), path: '/' },
        { name: t('footer.sections.platform.products'), path: '/products' },
        { name: t('footer.sections.platform.consultingServices'), path: '/consulting-services' },
        { name: t('footer.sections.platform.customLabServices'), path: '/custom-lab-services' },
        { name: t('footer.sections.platform.techSolutions'), path: '/tech-solutions' },
        { name: t('footer.sections.platform.contact'), path: '/contact' },
      ],
    },
    account: {
      title: t('footer.sections.account.title'),
      links: [
        { name: t('footer.sections.account.login'), path: '/login' },
        { name: t('footer.sections.account.register'), path: '/register' },
        { name: t('footer.sections.account.dashboard'), path: '/lab' },
      ],
    },
    legal: {
      title: t('footer.sections.legal.title'),
      links: [
        { name: t('footer.sections.legal.privacyPolicy'), path: '/privacy-policy' },
        { name: t('footer.sections.legal.refundPolicy'), path: '/refund-policy' },
      ],
    },
  };

  const socialLinks = [
    { icon: FaFacebook, url: 'https://facebook.com', color: 'hover:text-brand-blue' },
    { icon: FaTwitter, url: 'https://twitter.com', color: 'hover:text-blue-400' },
    { icon: FaInstagram, url: 'https://instagram.com', color: 'hover:text-pink-600' },
    { icon: FaLinkedin, url: 'https://linkedin.com', color: 'hover:text-blue-700' },
    { icon: FaYoutube, url: 'https://youtube.com', color: 'hover:text-red-600' },
    { icon: FaWhatsapp, url: 'https://whatsapp.com', color: 'hover:text-green-600' },
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Logo and Description */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <Logo size="lg" animated={true} />
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4 space-x-reverse">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`text-gray-400 ${social.color} transition-colors p-2 rounded-lg hover:bg-gray-800`}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {Object.values(footerLinks).map((section, index) => (
            <div key={index}>
              <h4 className="font-semibold text-lg mb-4 text-white">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact Info */}
        <div className="pt-8 border-t border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="flex items-center justify-center md:justify-start">
              <PhoneIcon className="w-6 h-6 text-brand-blue ml-3 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('footer.contact.phone')}</p>
                <p className="text-white font-medium" dir="ltr">+966532919535</p>
              </div>
            </div>
            <div className="flex items-center justify-center md:justify-start">
              <MailIcon className="w-6 h-6 text-brand-blue ml-3 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('footer.contact.email')}</p>
                <p className="text-white font-medium">info@badeil.com</p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                {t('footer.copyright', { year: currentYear })}
              </p>
              <div className="flex items-center text-sm text-gray-400">
                <span>{t('footer.madeWith')}</span>
                <HeartIcon className="w-4 h-4 text-red-500 mx-2" />
                <span>{t('footer.madeIn')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;